"use client";

import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Loader, CircleCheck, Users, MapPin, Lock } from "lucide-react";
export default function HeroWithLeadForm() {
  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    phone: "",
    desiredCity: "",
    whatAreYouLookingFor: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const features = [{
    icon: CircleCheck,
    text: "Get Prequalified Fast"
  }, {
    icon: Users,
    text: "Expert Local Agents"
  }, {
    icon: MapPin,
    text: "Personalized Support"
  }];
  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const {
      name,
      value
    } = event.target;
    setFormData(previous => ({
      ...previous,
      [name]: value
    }));
  };
  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setIsSubmitting(true);
    setSubmitError("");
    try {
      const response = await fetch("https://anish-leads.ngrok.app/webhook/fb-ad-10k-closing-credit", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          ...formData,
          source: "Check Eligibility $10K Closing Cost Page",
          pageUrl: "https://realestatewithanish.com/check-eligibility/"
        })
      });
      if (!response.ok) {
        throw new Error("Form submission failed");
      }
      setIsSubmitted(true);
    } catch (error) {
      setSubmitError("Something went wrong. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };
  return <section id="hero-with-lead-form" className="relative min-h-[85vh] flex items-center bg-background overflow-hidden py-16 lg:py-0">
      <div className="absolute inset-0 z-0">
        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/hero_residential_neighborhood.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/hero_residential_neighborhood.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1638281/img/hero_residential_neighborhood.png-wvc-sizes" alt="Beautiful residential neighborhood" className="w-full h-full object-cover object-right-top" loading="eager" />
        <div className="absolute inset-0 bg-gradient-to-r from-background via-background/95 to-background/40 lg:to-transparent"></div>
        <div className="absolute inset-0 bg-gradient-to-b from-background/80 via-transparent to-background/80 lg:hidden"></div>
      </div>

      <div className="container relative z-10 mx-auto px-4 md:px-6">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          <div className="lg:col-span-7 xl:col-span-6 space-y-8 lg:pr-8 order-first lg:order-first">
            <div className="flex flex-col sm:flex-row sm:items-center gap-6 border-b border-border/60 pb-6">
              <div className="flex items-center gap-6">
                <div className="flex flex-col items-center gap-2">
                  <img src="https://realestatewithanish.com/wp-content/uploads/2026/05/DreamRealty_Branding_Final-2.png" alt="Dream Realty Services" className="h-20 w-auto object-contain" />
                </div>
                <div className="w-px h-10 bg-border hidden sm:block"></div>
                <div className="flex flex-col items-center gap-2">
                  <img src="https://realestatewithanish.com/wp-content/uploads/2026/05/WhatsApp-Image-2023-12-11-at-1.26.09-PM-1.jpeg" alt="Genex Lending" className="h-20 w-auto object-contain" />
                </div>
              </div>
              <div className="sm:ml-auto text-base sm:text-lg font-medium text-foreground/80 sm:text-right">
                <span className="block">A Trusted Partnership.</span>
                <span className="block">Local Expertise. Powerful Lending.</span>
              </div>
            </div>

            <div className="space-y-6">
              <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-foreground font-default leading-[1.1] tracking-tight">
                You May Be Closer to <br className="hidden md:block" />
                <span className="text-[var(--accent-gold)]">Homeownership</span> <br className="hidden md:block" />
                <span className="block">Than You Think.</span>
              </h1>

              <p className="text-xl md:text-2xl text-foreground/80 max-w-2xl leading-relaxed">
                Explore homes across Naperville, Wheaton, Geneva, Schaumburg, St. Charles and nearby suburbs — *plus see if you qualify for up to{" "}
                <strong className="text-[var(--accent-gold)] font-bold">$10,000</strong> toward closing costs.
              </p>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 pt-4">
              {features.map((feature, i) => <div key={i} data-index={i} className="flex items-center gap-3 bg-background/50 backdrop-blur-sm p-3 rounded-lg border border-border/50 shadow-sm">
                  <feature.icon className="size-5 text-[var(--accent-gold)] shrink-0" />
                  <span className="text-base sm:text-lg font-semibold text-foreground">{feature.text}</span>
                </div>)}
            </div>
          </div>

          <div className="lg:col-span-5 xl:col-span-5 xl:col-start-8 w-full max-w-md mx-auto lg:mx-0 lg:ml-auto order-last lg:order-last">
            <Card className="bg-[#001a33] text-white border-none shadow-2xl overflow-hidden">
              <CardHeader className="pb-6 pt-8 px-8">
                <CardTitle className="text-3xl md:text-4xl font-bold text-white font-default leading-tight">
                  See If You Qualify for Up to <br />
                  <span className="text-[var(--accent-gold)]">$10,000</span> Toward <br />
                  Closing Costs*
                </CardTitle>
                <CardDescription className="text-white/80 text-lg mt-3">
                  Fill out the form and we'll be in touch to help you get started.
                </CardDescription>
              </CardHeader>

              <CardContent className="px-8 pb-8">
                {isSubmitted ? <div className="p-6 bg-green-50/10 border border-green-500/20 rounded-lg text-center">
                    <p className="text-green-400 font-medium">
                      Thank you! We have received your information and will be in touch shortly.
                    </p>
                  </div> : <form onSubmit={handleSubmit} className="space-y-4">
                    <input name="fullName" value={formData.fullName} onChange={handleChange} placeholder="Name *" required className="w-full bg-background text-foreground border-transparent focus-visible:ring-[var(--accent-gold)] h-12 rounded-md px-3" />

                    <input name="email" type="email" value={formData.email} onChange={handleChange} placeholder="Email *" required className="w-full bg-background text-foreground border-transparent focus-visible:ring-[var(--accent-gold)] h-12 rounded-md px-3" />

                    <input name="phone" type="tel" value={formData.phone} onChange={handleChange} placeholder="Phone *" required className="w-full bg-background text-foreground border-transparent focus-visible:ring-[var(--accent-gold)] h-12 rounded-md px-3" />

                    <input name="desiredCity" value={formData.desiredCity} onChange={handleChange} placeholder="Desired City" className="w-full bg-background text-foreground border-transparent focus-visible:ring-[var(--accent-gold)] h-12 rounded-md px-3" />

                    <input name="whatAreYouLookingFor" value={formData.whatAreYouLookingFor} onChange={handleChange} placeholder="What are you looking for?" className="w-full bg-background text-foreground border-transparent focus-visible:ring-[var(--accent-gold)] h-12 rounded-md px-3" />

                    {submitError && <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-md">
                        <p className="text-red-400 text-sm">{submitError}</p>
                      </div>}

                    <Button type="submit" disabled={isSubmitting} className="w-full bg-[var(--accent-gold)] hover:bg-[var(--accent-gold-dark)] text-foreground font-bold text-base h-12 mt-2 transition-colors">
                      {isSubmitting ? <>
                          <Loader className="animate-spin mr-2 size-5" />
                          Sending...
                        </> : "See if You Qualify"}
                    </Button>

                    <div className="flex items-center justify-center mt-3 text-white/60 text-sm">
                      <Lock className="size-4 mr-2" />
                      <span>No obligation. No hard credit pull.</span>
                    </div>

                    <div className="text-center text-xs text-gray-400 font-light mt-2">
                      * Programs subject to lender approval and availability.
                    </div>
                  </form>}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
}